class SinglePostPage {
  constructor() {
    this.progressBar = document.querySelector('.single-post__progress-bar');
    this.header = document.querySelector('.header');
    this.headerCategories = document.querySelector('.header-categories');
    this.sidebar = document.querySelector('.single-post__sidebar');
    this.tocList = document.getElementById('single-post-toc');
    this.contentArea = document.querySelector('.single-post__content');
    this.copyBtn = document.querySelector('.single-post__share-link--copy');
    this.copyFeedback = document.querySelector('.single-post__copy-feedback');
    this.copyFeedbackTimeout = null;

    this.modal = null;
    this.modalImage = null;
    this.lastBodyOverflow = '';
    this.recalcInterval = null;
    this.headings = [];
    this.tocLinks = [];
    this.activeTocLink = null;
    this.activeTocIndex = null;
    this.intersectionObserver = null;
    this.tocUpdateRafId = null;
    this.activeTocTimeouts = [];

    this.updateProgress = this.updateProgress.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.updateActiveTocLink = this.updateActiveTocLink.bind(this);
    this.scheduleTocUpdate = this.scheduleTocUpdate.bind(this);
    this.updateSidebarPosition = this.updateSidebarPosition.bind(this);
    this.updateTableScrollableState = this.updateTableScrollableState.bind(this);

    if (!this.progressBar && !this.tocList && !this.contentArea && !this.copyBtn) {
      return;
    }

    this.init();
  }

  init() {
    this.initProgressBar();
    this.initToc();
    this.initCopyButton();
    this.initLightboxAndTables();
    this.initSidebarPosition();

    if ('ontouchstart' in window) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          this.initTables();
        }, 200);
      });

      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          this.initTables();
        }, 300);
      });
    }
  }

  initProgressBar() {
    if (!this.progressBar) {
      return;
    }

    if (this.headerCategories) {
      this.headerCategories.appendChild(this.progressBar);
    }

    this.progressBar.classList.add('single-post__progress-bar--visible');

    this.cachedDocHeight = null;
    this.cachedViewportHeight = null;
    this.lastScrollY = 0;
    this.rafId = null;
    this.lastUpdateTime = 0;
    this.updateInterval = 16;

    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.updateProgress = this.updateProgress.bind(this);

    requestAnimationFrame(() => {
      this.updateProgress();
    });

    window.addEventListener('scroll', this.scheduleUpdate, {passive: true});
    window.addEventListener('scroll', this.scheduleTocUpdate, {passive: true});
    window.addEventListener('scroll', this.updateSidebarPosition, {passive: true});
    window.addEventListener('resize', this.handleResize.bind(this));

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.cachedDocHeight = null;
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  scheduleUpdate() {
    const now = performance.now();

    if (now - this.lastUpdateTime < this.updateInterval) {
      if (this.rafId !== null) {
        return;
      }
    }

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
    }

    this.rafId = requestAnimationFrame(() => {
      this.lastUpdateTime = performance.now();
      this.updateProgress();
      this.rafId = null;
    });
  }

  handleResize() {
    this.cachedDocHeight = null;
    this.cachedViewportHeight = null;
    this.scheduleUpdate();
  }

  updateProgress() {
    if (!this.progressBar) {
      return;
    }

    const scrollY = window.scrollY || window.pageYOffset;

    if (this.cachedViewportHeight === null) {
      this.cachedViewportHeight = window.innerHeight;
    }
    const viewportHeight = this.cachedViewportHeight;

    if (this.cachedDocHeight === null) {
      const doc = document.documentElement;
      this.cachedDocHeight = doc.scrollHeight;
    }

    const docHeight = this.cachedDocHeight;
    const maxScroll = Math.max(1, docHeight - viewportHeight);
    const clampedProgress = Math.max(0, Math.min(1, scrollY / maxScroll));

    this.progressBar.style.setProperty('--single-post-progress', `${clampedProgress * 100}%`);
  }

  initToc() {
    if (!this.tocList || !this.contentArea) {
      return;
    }

    const headings = this.contentArea.querySelectorAll('h2');

    if (headings.length > 0) {
      headings.forEach((heading, index) => {
        if (!heading.id) {
          heading.id = `toc-heading-${index}`;
        }

        const li = document.createElement('li');
        const a = document.createElement('a');
        a.classList.add('underlined-link');

        a.href = `#${heading.id}`;
        a.textContent = heading.textContent;

        a.addEventListener('click', (event) => {
          event.preventDefault();

          const currentScrollY = window.scrollY || window.pageYOffset;
          const headingTop = heading.getBoundingClientRect().top + window.pageYOffset;
          const offset = this.calculateScrollOffset(headingTop);

          window.scrollTo({
            top: headingTop - offset,
            behavior: 'smooth'
          });
        });

        li.appendChild(a);
        this.tocList.appendChild(li);

        this.headings.push(heading);
        this.tocLinks.push(a);
      });

      this.initActiveTocTracking();
    } else {
      const tocCard = this.tocList.closest('.single-post__sidebar-card');
      if (tocCard) {
        tocCard.style.display = 'none';
      }
    }
  }

  initActiveTocTracking() {
    if (this.headings.length === 0) {
      return;
    }

    const offset = this.calculateScrollOffset();

    const observerOptions = {
      root: null,
      rootMargin: `-${offset}px 0px -50% 0px`,
      threshold: [0, 0.1, 0.5, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      let visibleHeading = null;

      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
          if (!visibleHeading || entry.boundingClientRect.top < visibleHeading.boundingClientRect.top) {
            visibleHeading = entry;
          }
        }
      });

      if (!visibleHeading && entries.length > 0) {
        const lastEntry = entries[entries.length - 1];
        if (lastEntry.boundingClientRect.top < offset) {
          visibleHeading = lastEntry;
        }
      }

      if (visibleHeading) {
        const headingIndex = this.headings.indexOf(visibleHeading.target);
        if (headingIndex !== -1) {
          this.updateActiveTocLink(headingIndex);
        }
      }
    }, observerOptions);

    this.headings.forEach(heading => {
      this.intersectionObserver.observe(heading);
    });

    requestAnimationFrame(() => {
      this.updateActiveTocLink();
    });
  }

  scheduleTocUpdate() {
    if (this.tocUpdateRafId !== null) {
      cancelAnimationFrame(this.tocUpdateRafId);
    }

    this.tocUpdateRafId = requestAnimationFrame(() => {
      this.updateActiveTocLink();
      this.tocUpdateRafId = null;
    });
  }

  calculateScrollOffset(targetScrollY = null) {
    let offset = 24;

    if (this.headerCategories) {
      offset += this.headerCategories.offsetHeight;
    }

    if (this.header) {
      const currentScrollY = window.scrollY || window.pageYOffset;
      const isScrollingUp = targetScrollY !== null ? currentScrollY > targetScrollY : false;
      const isHeaderVisible = !this.header.classList.contains('header--hidden');

      if (isScrollingUp || (isHeaderVisible && targetScrollY === null)) {
        offset += this.header.offsetHeight;
      }
    }

    return offset;
  }

  updateActiveTocLink(activeIndex = null) {
    if (activeIndex === null) {
      const offset = this.calculateScrollOffset();
      const scrollY = window.scrollY || window.pageYOffset;
      const viewportTop = scrollY + offset;

      let foundIndex = null;

      for (let i = this.headings.length - 1; i >= 0; i--) {
        const headingRect = this.headings[i].getBoundingClientRect();
        const headingTop = headingRect.top + scrollY;

        if (headingTop <= viewportTop + 100) {
          foundIndex = i;
          break;
        }
      }

      if (foundIndex === null && scrollY < 200) {
        foundIndex = 0;
      }

      activeIndex = foundIndex;
    }

    if (activeIndex === this.activeTocIndex) {
      return;
    }

    this.activeTocTimeouts.forEach(timeout => clearTimeout(timeout));
    this.activeTocTimeouts = [];

    if (this.activeTocLink) {
      this.activeTocLink.classList.remove('single-post__toc-link--active');
      const prevLineWrappers = this.activeTocLink.querySelectorAll('.line-wrapper');
      prevLineWrappers.forEach(wrapper => {
        wrapper.classList.remove('underlined-link');
      });
    }

    if (activeIndex !== null && activeIndex >= 0 && activeIndex < this.tocLinks.length) {
      this.activeTocLink = this.tocLinks[activeIndex];
      this.activeTocIndex = activeIndex;
      this.activeTocLink.classList.add('single-post__toc-link--active');

      const lineWrappers = this.activeTocLink.querySelectorAll('.line-wrapper');
      lineWrappers.forEach((wrapper, index) => {
        const timeout = setTimeout(() => {
          wrapper.classList.add('underlined-link');
        }, index * 250);
        this.activeTocTimeouts.push(timeout);
      });
    } else {
      this.activeTocIndex = null;
    }
  }

  initCopyButton() {
    if (!this.copyBtn) {
      return;
    }

    this.copyBtn.addEventListener('click', (event) => {
      event.preventDefault();
      const url = this.copyBtn.dataset.copyUrl || window.location.href;

      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        return;
      }

      navigator.clipboard.writeText(url).then(() => {
        this.showCopyFeedback();
      }).catch(() => {});
    });
  }

  showCopyFeedback() {
    if (!this.copyFeedback) {
      return;
    }

    this.copyFeedback.setAttribute('aria-hidden', 'false');
    this.copyFeedback.classList.add('single-post__copy-feedback--visible');

    if (this.copyFeedbackTimeout !== null) {
      clearTimeout(this.copyFeedbackTimeout);
    }

    this.copyFeedbackTimeout = setTimeout(() => {
      this.copyFeedback.classList.remove('single-post__copy-feedback--visible');
      this.copyFeedback.setAttribute('aria-hidden', 'true');
      this.copyFeedbackTimeout = null;
    }, 2500);
  }

  initLightboxAndTables() {
    if (!this.contentArea) {
      return;
    }

    this.initImagesLightbox();
    this.initTables();
  }

  initImagesLightbox() {
    const images = this.contentArea.querySelectorAll('img');

    if (images.length === 0) {
      return;
    }

    images.forEach((img) => {
      const link = img.closest('a');
      const shouldOpenModal = this.shouldOpenImageInModal(img, link);

      if (!shouldOpenModal) {
        return;
      }

      img.style.cursor = 'zoom-in';

      img.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        this.ensureModal();
        if (!this.modal || !this.modalImage) {
          return;
        }

        this.modalImage.src = img.currentSrc || img.src;
        this.modalImage.alt = img.alt || '';

        this.lastBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        this.modal.classList.add('single-post__image-modal--visible');
        document.addEventListener('keydown', this.onKeyDown);
      });
    });
  }

  shouldOpenImageInModal(img, link) {
    if (!link) {
      return true;
    }

    const href = this.normalizeUrl(link.getAttribute('href'));
    if (!href) {
      return false;
    }

    const imageUrls = this.getImageCandidateUrls(img);
    if (imageUrls.includes(href)) {
      return true;
    }

    // WordPress links to full-size original, which is absent from srcset (srcset has only resized variants)
    return /\.(jpe?g|png|gif|webp|avif)$/i.test(href);
  }

  getImageCandidateUrls(img) {
    const urls = [];

    if (img.currentSrc) {
      urls.push(img.currentSrc);
    }
    if (img.src) {
      urls.push(img.src);
    }

    const srcset = img.getAttribute('srcset');
    if (srcset) {
      srcset.split(',').forEach((candidate) => {
        const candidateUrl = candidate.trim().split(/\s+/)[0];
        if (candidateUrl) {
          urls.push(candidateUrl);
        }
      });
    }

    return [...new Set(urls.map((url) => this.normalizeUrl(url)).filter(Boolean))];
  }

  normalizeUrl(url) {
    if (!url) {
      return null;
    }

    try {
      const parsedUrl = new URL(url, window.location.href);
      return `${parsedUrl.origin}${parsedUrl.pathname}`;
    } catch (error) {
      return null;
    }
  }

  updateTableScrollableState(wrapper, table) {
    setTimeout(() => {
      const rowsCount = table.querySelectorAll('tr').length;
      const isSingleRowTable = rowsCount === 1;
      const needsScroll = !isSingleRowTable && table.scrollWidth > wrapper.clientWidth + 1;

      wrapper.classList.toggle('single-post__table--single-row', isSingleRowTable);
      wrapper.classList.toggle('single-post__table--scrollable', needsScroll);

      if (needsScroll) {
        wrapper.setAttribute('data-scrollable', 'true');
      } else {
        wrapper.removeAttribute('data-scrollable');
      }

      this.updateTableProgress(wrapper);
    }, 100);
  }

  initTables() {
    const tables = this.contentArea.querySelectorAll('.wp-block-table');

    tables.forEach((wrapper) => {
      const hasThead = wrapper.querySelector('thead');
      if (!hasThead) {
        wrapper.classList.add('single-post__table--no-head');
      }

      const table = wrapper.querySelector('table');
      if (!table) {
        return;
      }

      const updateState = () => this.updateTableScrollableState(wrapper, table);
      updateState();
      window.addEventListener('load', updateState);
      setTimeout(updateState, 500);
      window.addEventListener('resize', updateState, {passive: true});

      if ('ontouchstart' in window) {
        wrapper.addEventListener('touchstart', () => {
          wrapper.classList.add('single-post__table--touching');
        }, {passive: true});

        wrapper.addEventListener('touchend', () => {
          wrapper.classList.remove('single-post__table--touching');
        }, {passive: true});

        wrapper.addEventListener('touchcancel', () => {
          wrapper.classList.remove('single-post__table--touching');
        }, {passive: true});
      }

      if (window.MutationObserver) {
        const observer = new MutationObserver(updateState);
        observer.observe(table, {
          childList: true,
          subtree: true,
          attributes: true
        });
      }

    });
  }

  ensureModal() {
    if (this.modal) {
      return;
    }

    const modal = document.createElement('div');
    modal.className = 'single-post__image-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    modal.innerHTML = `
                <div class="single-post__image-modal-inner">
                    <img class="single-post__image-modal-image" src="" alt="" />
                </div>
                <button type="button" class="single-post__image-modal-close" aria-label="Close image">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="0.5" y="0.5" width="63" height="63" rx="31.5" stroke="black"/>
                        <path d="M21.375 21.375L42.625 42.625" stroke="black"/>
                        <path d="M21.375 42.625L42.625 21.375" stroke="black"/>
                    </svg>
                </button>
            `;

    this.modal = modal;
    this.modalImage = modal.querySelector('.single-post__image-modal-image');
    const closeBtn = modal.querySelector('.single-post__image-modal-close');

    if (closeBtn) {
      closeBtn.addEventListener('click', this.closeModal);
    }

    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        this.closeModal();
      }
    });

    document.body.appendChild(modal);
  }

  closeModal() {
    if (!this.modal) {
      return;
    }

    this.modal.classList.remove('single-post__image-modal--visible');
    document.removeEventListener('keydown', this.onKeyDown);
    document.body.style.overflow = this.lastBodyOverflow;
  }

  onKeyDown(event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }

  initSidebarPosition() {
    if (!this.sidebar) {
      return;
    }

    this.updateSidebarPosition();
  }

  updateSidebarPosition() {
    if (!this.sidebar) {
      return;
    }

    let top = 24;

    if (this.headerCategories) {
      top += this.headerCategories.offsetHeight;
    }

    if (this.header && !this.header.classList.contains('header--hidden')) {
      top += this.header.offsetHeight;
    }

    this.sidebar.style.top = `${top}px`;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SinglePostPage();
  });
} else {
  new SinglePostPage();
}